import { useEffect, useMemo, useState } from "react";
import { useAuth } from "react-oidc-context";
import { useTranslation } from "react-i18next";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPlus, faTimes } from "@fortawesome/pro-regular-svg-icons";
import { Popover, Tooltip } from "../../common";
import AnalyticsFilterInputs from "../../../types/analytics/AnalyticsFilterInputs";
import userGroupApi from "../../../api/admin/userGroupApi";
import locationApi from "../../../api/dashboard/locationApi";
import jobTitleApi from "../../../api/dashboard/jobTitleApi";
import appraisalLevelApi from "../../../api/dashboard/appraisalLevelApi";
import CheckboxTreeNodeDto from "../../../types/dtos/generic/CheckboxTreeNodeDto";
import FilterCheckboxTreeApiResponseDto from "../../../types/analytics/FilterCheckboxTreeApiResponseDto";
import AudienceFacetEditor from "./AudienceFacetEditor";
import {
  AudienceFacetType,
  audienceFacetLabel,
  formatAudienceFacetSummary,
  labelMapFromCheckboxTree,
} from "../../../helpers/analytics/audienceFacetHelpers";
import { ANALYTICS_CUSTOM_FIELDS_FACET } from "../../../config/analytics/analyticsFilterShipFlags";

interface AudienceFacetChipBarProps {
  selectedValues: AnalyticsFilterInputs;
  chipClassName(dirty: boolean, quiet?: boolean): string;
  isFacetDirty(type: AudienceFacetType): boolean;
  onLocationPickerChange(selectedLocationIds: number[]): void;
  onJobtitlePickerChange(selectedJobtitleIds: number[]): void;
  onAppraisalLevelPickerChange(selectedAppraisalLevelIds: number[]): void;
  onUserGroupPickerChange(selectedUserGroupIds: number[]): void;
  /** True while any audience facet popover is open (gates band Apply/Reset). */
  onFacetPopoverOpenChange?(open: boolean): void;
}

const ALL_FACETS: AudienceFacetType[] = [
  "APPRAISAL-LEVEL",
  "LOCATION",
  "JOB-TITLE",
  "USER-GROUP",
];

function facetsWithSelections(
  values: AnalyticsFilterInputs,
): AudienceFacetType[] {
  const result: AudienceFacetType[] = [];
  if ((values.appraisalLevelIds?.length ?? 0) > 0) {
    result.push("APPRAISAL-LEVEL");
  }
  if ((values.locationIds?.length ?? 0) > 0) {
    result.push("LOCATION");
  }
  if ((values.jobTitleIds?.length ?? 0) > 0) {
    result.push("JOB-TITLE");
  }
  if ((values.userGroupIds?.length ?? 0) > 0) {
    result.push("USER-GROUP");
  }
  return result;
}

function selectedIdsForFacet(
  type: AudienceFacetType,
  values: AnalyticsFilterInputs,
): number[] | null | undefined {
  switch (type) {
    case "APPRAISAL-LEVEL":
      return values.appraisalLevelIds;
    case "LOCATION":
      return values.locationIds;
    case "JOB-TITLE":
      return values.jobTitleIds;
    case "USER-GROUP":
      return values.userGroupIds;
  }
}

function sameIdLists(
  a: number[] | null | undefined,
  b: number[] | null | undefined,
): boolean {
  const aa = [...(a ?? [])].map(Number).sort((x, y) => x - y);
  const bb = [...(b ?? [])].map(Number).sort((x, y) => x - y);
  return aa.length === bb.length && aa.every((v, i) => v === bb[i]);
}

function AudienceFacetChipBar({
  selectedValues,
  chipClassName,
  isFacetDirty,
  onLocationPickerChange,
  onJobtitlePickerChange,
  onAppraisalLevelPickerChange,
  onUserGroupPickerChange,
  onFacetPopoverOpenChange,
}: AudienceFacetChipBarProps) {
  const { t } = useTranslation();
  const auth = useAuth();
  const token = auth.user?.access_token;

  const [visibleFacets, setVisibleFacets] = useState<AudienceFacetType[]>(() =>
    facetsWithSelections(selectedValues),
  );
  const [openFacet, setOpenFacet] = useState<AudienceFacetType | null>(null);
  /** Bumps when a facet opens so the editor remounts with a fresh draft. */
  const [editorEpoch, setEditorEpoch] = useState(0);
  const [addMenuOpen, setAddMenuOpen] = useState(false);
  const [hasUserGroups, setHasUserGroups] = useState(false);

  const [locationTree, setLocationTree] = useState<CheckboxTreeNodeDto[]>();
  const [jobTitleLabels, setJobTitleLabels] = useState<Map<number, string>>();
  const [appraisalLevelLabels, setAppraisalLevelLabels] =
    useState<Map<number, string>>();
  const [userGroupLabels, setUserGroupLabels] =
    useState<Map<number, string>>();

  useEffect(() => {
    onFacetPopoverOpenChange?.(openFacet != null);
  }, [openFacet, onFacetPopoverOpenChange]);

  useEffect(() => {
    new userGroupApi(token).getAnalyticsUserGroups(
      (data) => {
        setHasUserGroups(data.length > 0);
        setUserGroupLabels(
          new Map(data.map((g) => [g.id, g.name] as [number, string])),
        );
      },
      (error) => console.error(error),
    );
  }, [token]);

  useEffect(() => {
    if (!visibleFacets.includes("LOCATION") || locationTree !== undefined) {
      return;
    }
    new locationApi(token).getLocationCheckboxTreeItemsByClientId(
      false,
      (data: FilterCheckboxTreeApiResponseDto) => setLocationTree(data.nodes),
      (error) => console.error(error),
    );
  }, [visibleFacets, locationTree, token]);

  useEffect(() => {
    if (!visibleFacets.includes("JOB-TITLE") || jobTitleLabels !== undefined) {
      return;
    }
    new jobTitleApi(token).getJobTitleCheckboxTreeItemsByClientId(
      (data: FilterCheckboxTreeApiResponseDto) =>
        setJobTitleLabels(labelMapFromCheckboxTree(data.nodes)),
      (error) => console.error(error),
    );
  }, [visibleFacets, jobTitleLabels, token]);

  useEffect(() => {
    if (
      !visibleFacets.includes("APPRAISAL-LEVEL") ||
      appraisalLevelLabels !== undefined
    ) {
      return;
    }
    new appraisalLevelApi(token).getAppraisalLevelCheckboxTreeItemsByClientId(
      (data: FilterCheckboxTreeApiResponseDto) =>
        setAppraisalLevelLabels(labelMapFromCheckboxTree(data.nodes)),
      (error) => console.error(error),
    );
  }, [visibleFacets, appraisalLevelLabels, token]);

  // Keep chips for staged selections; keep an open draft chip from +.
  useEffect(() => {
    setVisibleFacets((prev) => {
      const withSelections = facetsWithSelections(selectedValues);
      const next = prev.filter(
        (facet) => withSelections.includes(facet) || facet === openFacet,
      );
      for (const facet of withSelections) {
        if (!next.includes(facet)) next.push(facet);
      }
      return next;
    });
  }, [
    selectedValues.appraisalLevelIds,
    selectedValues.locationIds,
    selectedValues.jobTitleIds,
    selectedValues.userGroupIds,
    openFacet,
  ]);

  const addableFacets = useMemo(
    () =>
      ALL_FACETS.filter((f) => {
        if (visibleFacets.includes(f)) return false;
        if (f === "USER-GROUP" && !hasUserGroups) return false;
        return true;
      }),
    [visibleFacets, hasUserGroups],
  );

  const labelByIdFor = (type: AudienceFacetType) => {
    switch (type) {
      case "APPRAISAL-LEVEL":
        return appraisalLevelLabels;
      case "JOB-TITLE":
        return jobTitleLabels;
      case "USER-GROUP":
        return userGroupLabels;
      case "LOCATION":
        return locationTree
          ? labelMapFromCheckboxTree(locationTree)
          : undefined;
    }
  };

  const summaryFor = (type: AudienceFacetType) =>
    formatAudienceFacetSummary({
      type,
      selectedIds: selectedIdsForFacet(type, selectedValues),
      t,
      labelById: labelByIdFor(type),
      locationTree: type === "LOCATION" ? locationTree : undefined,
    });

  const openFacetEditor = (type: AudienceFacetType) => {
    setEditorEpoch((n) => n + 1);
    setOpenFacet(type);
  };

  const clearFacet = (type: AudienceFacetType) => {
    switch (type) {
      case "APPRAISAL-LEVEL":
        onAppraisalLevelPickerChange([]);
        break;
      case "LOCATION":
        onLocationPickerChange([]);
        break;
      case "JOB-TITLE":
        onJobtitlePickerChange([]);
        break;
      case "USER-GROUP":
        onUserGroupPickerChange([]);
        break;
    }
    setVisibleFacets((prev) => prev.filter((f) => f !== type));
    if (openFacet === type) setOpenFacet(null);
  };

  const addFacet = (type: AudienceFacetType) => {
    setVisibleFacets((prev) =>
      prev.includes(type) ? prev : [...prev, type],
    );
    setAddMenuOpen(false);
    window.setTimeout(() => openFacetEditor(type), 50);
  };

  const onIdsChange = (type: AudienceFacetType, ids: number[]) => {
    switch (type) {
      case "APPRAISAL-LEVEL":
        onAppraisalLevelPickerChange(ids);
        break;
      case "LOCATION":
        onLocationPickerChange(ids);
        break;
      case "JOB-TITLE":
        onJobtitlePickerChange(ids);
        break;
      case "USER-GROUP":
        onUserGroupPickerChange(ids);
        break;
    }
  };

  const commitFacetDraft = (facet: AudienceFacetType, ids: number[]) => {
    const staged = selectedIdsForFacet(facet, selectedValues) ?? [];
    if (ids.length === 0) {
      if (staged.length > 0) {
        clearFacet(facet);
      } else {
        // New + facet dismissed/Done with nothing selected — no staged chip.
        setVisibleFacets((prev) => prev.filter((f) => f !== facet));
        setOpenFacet(null);
      }
      return;
    }
    if (!sameIdLists(ids, staged)) {
      onIdsChange(facet, ids);
    }
    setOpenFacet(null);
  };

  return (
    <>
      {visibleFacets.map((facet, idx) => (
        <Popover
          key={facet}
          openState={openFacet === facet}
          onOpenStateChange={(open) => {
            if (open) {
              openFacetEditor(facet);
            } else {
              // Dismiss without Done — discard draft (editor remounts next open).
              setOpenFacet(null);
            }
          }}
          showCloseIcon={false}
          contentClassNames="w-80 bg-white p-3 border border-gray-200 shadow-lg-around"
          triggerElement={
            <button
              type="button"
              className={`${chipClassName(isFacetDirty(facet), true)}${
                isFacetDirty(facet) && idx > 0 ? " ml-1" : ""
              }`}
            >
              <span className="text-xs font-normal text-gray-500">
                {audienceFacetLabel(facet, t)}
              </span>
              <span className="max-w-[10rem] truncate">{summaryFor(facet)}</span>
              <span
                role="button"
                tabIndex={0}
                className="ml-0.5 inline-flex h-4 w-4 items-center justify-center rounded text-gray-400 hover:bg-gray-200 hover:text-gray-700"
                aria-label={t(
                  "Analytics.Filters.RemoveFacet",
                  "Remove filter",
                )}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  clearFacet(facet);
                }}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    e.stopPropagation();
                    clearFacet(facet);
                  }
                }}
              >
                <FontAwesomeIcon icon={faTimes} className="text-[10px]" />
              </span>
            </button>
          }
        >
          <div>
            <h5 className="text-sm font-bold mb-2">
              {audienceFacetLabel(facet, t)}
            </h5>
            {openFacet === facet && (
              <AudienceFacetEditor
                key={`${facet}-${editorEpoch}`}
                facetType={facet}
                selectedIds={selectedIdsForFacet(facet, selectedValues)}
                commitOnChange={false}
                showDoneButton
                onDone={(ids) => commitFacetDraft(facet, ids)}
              />
            )}
          </div>
        </Popover>
      ))}

      <Popover
        openState={addMenuOpen}
        onOpenStateChange={setAddMenuOpen}
        showCloseIcon={false}
        contentClassNames="w-56 bg-white p-2 border border-gray-200 shadow-lg-around"
        triggerElement={
          <span className="inline-flex">
            <Tooltip
              content={t("Analytics.Filters.AddFilter", "Add filter")}
              triggerElement={
                <button
                  type="button"
                  className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-gray-200 bg-white text-gray-500 hover:border-gray-300 hover:text-gray-800"
                  aria-label={t("Analytics.Filters.AddFilter", "Add filter")}
                >
                  <FontAwesomeIcon icon={faPlus} className="text-sm" />
                </button>
              }
            />
          </span>
        }
      >
        <div className="py-1">
          <div className="px-2 pb-1 text-[11px] font-semibold uppercase tracking-wide text-gray-400">
            {t("Analytics.Filters.AddFilter", "Add filter")}
          </div>
          {addableFacets.map((facet) => (
            <button
              key={facet}
              type="button"
              className="w-full rounded-md px-2 py-1.5 text-left text-sm font-medium text-gray-800 hover:bg-gray-100"
              onClick={() => addFacet(facet)}
            >
              {audienceFacetLabel(facet, t)}
            </button>
          ))}
          {ANALYTICS_CUSTOM_FIELDS_FACET && (
            <button
              type="button"
              disabled
              className="w-full rounded-md px-2 py-1.5 text-left text-sm font-medium text-gray-400 cursor-not-allowed"
              title={t(
                "Analytics.Filters.CustomFieldsStub",
                "Custom fields - coming later",
              )}
            >
              {t("Analytics.Filters.CustomFields", "Custom fields")}
            </button>
          )}
          {addableFacets.length === 0 && (
            <p className="px-2 py-1 text-xs text-gray-400">
              {t(
                "Analytics.Filters.AllFacetsAdded",
                "All available audience filters are on the bar.",
              )}
            </p>
          )}
        </div>
      </Popover>
    </>
  );
}

export default AudienceFacetChipBar;
